import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { CalendarDays, Home, Layers, Mail, Mic2, Settings, Vault, Zap, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/app", label: "Today", icon: Home, end: true },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/app/emails", label: "Emails", icon: Mail },
  { to: "/app/campaigns", label: "Campaigns", icon: Layers },
  { to: "/app/vaults", label: "Vaults", icon: Vault },
  { to: "/app/voice", label: "Voice", icon: Mic2 },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export const Logo = ({ light = false }: { light?: boolean }) => (
  <Link to="/app" className={`font-serif text-2xl tracking-tight ${light ? "text-cream" : "text-navy"}`} data-testid="logo">
    Email <span className="font-accent text-[1.35em] leading-none align-[-0.08em] text-olive">Bestie</span>
  </Link>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-cream lg:grid lg:grid-cols-[240px_1fr]">
      <aside className="hidden lg:flex flex-col bg-navy text-cream px-5 py-6 sticky top-0 h-screen" data-testid="sidebar">
        <Logo light />
        <p className="text-xs text-sand mt-1 mb-8">Your AI email marketing manager</p>
        <nav className="flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-olive-light text-navy" : "text-sand hover:bg-cream/10 hover:text-cream"}`}>
              <n.icon className="size-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <Link to="/app/quick" className="btn-clay mt-6 w-full" data-testid="nav-quick-email"><Zap className="size-4" /> I need an email now</Link>
        <div className="mt-auto pt-6 border-t border-cream/15">
          <p className="text-sm font-semibold text-cream truncate">{user?.name}</p>
          <p className="text-xs text-sand truncate">{user?.email}</p>
          <button onClick={doLogout} className="mt-3 text-xs text-sand hover:text-cream inline-flex items-center gap-1" data-testid="logout-button"><LogOut className="size-3" /> Log out</button>
        </div>
      </aside>
      <div className="flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-navy text-cream" data-testid="mobile-header">
          <Logo light />
          <div className="flex items-center gap-2">
            <Link to="/app/quick" className="btn-clay !px-3 !py-2 text-xs" data-testid="mobile-quick-email"><Zap className="size-3.5" /> Email now</Link>
            <Link to="/app/settings" className="size-9 grid place-items-center rounded-full bg-cream/10 text-cream hover:bg-cream/20" aria-label="Settings" data-testid="mobile-settings-link"><Settings className="size-4" /></Link>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-navy border-t border-cream/10 grid grid-cols-5" data-testid="mobile-nav">
          {NAV.slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${isActive ? "text-olive-light" : "text-sand/80"}`}>
              <n.icon className="size-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
